import { useEffect, useState } from "react";
import { api, fmtNumber, fmtDateTime, parseApiError } from "@/lib/api";
import { PageHeader } from "./AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { DataStateRow } from "@/components/ui/data-state";
import { Package, ArrowDownToLine, ArrowUpFromLine, Repeat, AlertTriangle, Plus } from "lucide-react";
import { toast } from "sonner";
import { validateStockMovement } from "@/lib/business-validation";

export default function Stock() {
  const [levels, setLevels] = useState([]);
  const [moves, setMoves] = useState([]);
  const [branches, setBranches] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ sku_id: "", movement_type: "in", qty: 1, branch_id: "", unit_cost: 0, note: "" });
  const [fieldErrors, setFieldErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const load = async () => {
    setLoading(true);
    setLoadError("");
    try {
      const [levelsRes, movesRes] = await Promise.all([
        api.get("/stock/levels"),
        api.get("/stock/movements?days=30"),
      ]);
      setLevels(levelsRes.data);
      setMoves(movesRes.data);
    } catch (e) {
      setLoadError(parseApiError(e, "Không tải được dữ liệu kho").message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
    api.get("/branches").then((r) => setBranches(r.data));
  }, []);

  const submitMove = async () => {
    try {
      const payload = { ...form, qty: Number(form.qty), unit_cost: Number(form.unit_cost) };
      const validation = validateStockMovement(payload);
      if (!validation.ok) {
        setFieldErrors(validation.fieldErrors);
        toast.error(validation.message);
        return;
      }
      await api.post("/stock/movements", payload);
      toast.success("Đã ghi nhận"); setOpen(false); load();
    } catch (e) {
      const parsed = parseApiError(e, "Lỗi");
      setFieldErrors(parsed.fieldErrors);
      toast.error(parsed.message);
    }
  };

  const setField = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
    setFieldErrors((errors) => ({ ...errors, [field]: undefined }));
  };

  const lowStock = levels.filter((s) => Number(s.stock) < 20).length;

  return (
    <div data-testid="page-stock">
      <PageHeader
        kicker="P3 • Vận hành"
        title="Kho tổng & Tồn kho"
        breadcrumb={["Vận hành", "Kho tổng"]}
        actions={
          <Button onClick={() => { setFieldErrors({}); setOpen(true); }} className="bg-emerald-800 hover:bg-emerald-900 rounded-sm" data-testid="btn-stock-move">
            <Plus className="w-4 h-4 mr-1.5" />Nhập / xuất kho
          </Button>
        }
      />
      <div className="p-6 lg:p-8 space-y-4">
        {lowStock > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-sm p-3 flex items-center gap-2 text-sm">
            <AlertTriangle className="w-4 h-4 text-amber-700" />
            <span><strong>{lowStock}</strong> SKU đang có tồn dưới ngưỡng 20.</span>
          </div>
        )}

        <Tabs defaultValue="levels">
          <TabsList className="bg-stone-100 rounded-sm">
            <TabsTrigger value="levels" className="rounded-sm data-[state=active]:bg-white" data-testid="tab-levels">Tồn kho hiện tại</TabsTrigger>
            <TabsTrigger value="moves" className="rounded-sm data-[state=active]:bg-white" data-testid="tab-moves">Lịch sử di chuyển</TabsTrigger>
          </TabsList>

          <TabsContent value="levels" className="mt-3">
            <div className="bg-white border border-stone-200 rounded-sm overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-stone-50 border-b border-stone-200">
                  <tr>
                    {["Mã","Tên","Đơn vị","Nhóm","Tồn","Giá vốn","Giá bán","Giá trị tồn"].map(h => <th key={h} className="text-left px-4 py-2.5 text-[10px] uppercase tracking-wider text-stone-600 font-bold">{h}</th>)}
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-100">
                  {!loading && !loadError && levels.map((s) => (
                    <tr key={s.id} className="hover:bg-stone-50" data-testid={`level-${s.code}`}>
                      <td className="px-4 py-2 font-mono text-xs text-stone-600">{s.code}</td>
                      <td className="px-4 py-2 font-semibold">{s.name}</td>
                      <td className="px-4 py-2 text-stone-600">{s.unit}</td>
                      <td className="px-4 py-2 text-stone-600 text-xs">{s.category || "—"}</td>
                      <td className="px-4 py-2 font-mono text-sm font-bold">
                        <span className={Number(s.stock) < 20 ? "text-amber-700" : ""}>{fmtNumber(s.stock)}</span>
                      </td>
                      <td className="px-4 py-2 font-mono text-xs">{fmtNumber(s.cost)}</td>
                      <td className="px-4 py-2 font-mono text-xs">{fmtNumber(s.price)}</td>
                      <td className="px-4 py-2 font-mono text-xs">{fmtNumber(Number(s.stock) * Number(s.cost))}</td>
                    </tr>
                  ))}
                  {loading && <DataStateRow colSpan={8} type="loading" message="Đang tải tồn kho..." />}
                  {!loading && loadError && <DataStateRow colSpan={8} type="error" message={loadError} onRetry={load} />}
                  {!loading && !loadError && levels.length === 0 && <DataStateRow colSpan={8} type="empty" message="Chưa có dữ liệu tồn kho" />}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="moves" className="mt-3">
            <div className="bg-white border border-stone-200 rounded-sm overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-stone-50 border-b border-stone-200">
                  <tr>
                    {["Thời gian","SKU","Loại","SL","Chi nhánh","Tham chiếu","Người thực hiện","Ghi chú"].map(h => <th key={h} className="text-left px-4 py-2.5 text-[10px] uppercase tracking-wider text-stone-600 font-bold">{h}</th>)}
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-100">
                  {!loading && !loadError && moves.map((m) => (
                    <tr key={m.id} className="hover:bg-stone-50">
                      <td className="px-4 py-2 text-xs font-mono">{fmtDateTime(m.created_at)}</td>
                      <td className="px-4 py-2"><span className="font-mono text-xs text-stone-500">{m.sku_code}</span> <span className="font-semibold">{m.sku_name}</span></td>
                      <td className="px-4 py-2">
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-sm font-bold ${
                          m.movement_type === "in" || m.movement_type === "transfer_in" ? "bg-emerald-100 text-emerald-900" :
                          m.movement_type === "out" || m.movement_type === "transfer_out" ? "bg-red-100 text-red-900" :
                          "bg-stone-200 text-stone-800"}`}>{m.movement_type}</span>
                      </td>
                      <td className="px-4 py-2 font-mono text-sm font-bold">{fmtNumber(m.qty)}</td>
                      <td className="px-4 py-2 text-xs text-stone-600">{m.branch_name || "—"}</td>
                      <td className="px-4 py-2 text-xs text-stone-500">{m.ref_type || "—"}</td>
                      <td className="px-4 py-2 text-xs">{m.user_name || "—"}</td>
                      <td className="px-4 py-2 text-xs text-stone-500">{m.note || "—"}</td>
                    </tr>
                  ))}
                  {loading && <DataStateRow colSpan={8} type="loading" message="Đang tải lịch sử di chuyển kho..." />}
                  {!loading && loadError && <DataStateRow colSpan={8} type="error" message={loadError} onRetry={load} />}
                  {!loading && !loadError && moves.length === 0 && <DataStateRow colSpan={8} type="empty" message="Chưa có di chuyển kho" />}
                </tbody>
              </table>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm">
          <DialogHeader><DialogTitle className="font-serif text-2xl">Ghi nhận di chuyển kho</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">SKU *</Label>
              <Select value={form.sku_id} onValueChange={(v) => setField("sku_id", v)}>
                <SelectTrigger aria-invalid={Boolean(fieldErrors.sku_id)} className={`h-9 rounded-sm mt-1 ${fieldErrors.sku_id ? "border-red-400 focus:ring-red-500" : ""}`}><SelectValue placeholder="Chọn SKU" /></SelectTrigger>
                <SelectContent>{levels.map((s) => <SelectItem key={s.id} value={s.id}>{s.code} - {s.name} (tồn: {s.stock})</SelectItem>)}</SelectContent>
              </Select>
              <FieldError message={fieldErrors.sku_id} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Loại</Label>
                <Select value={form.movement_type} onValueChange={(v) => setField("movement_type", v)}>
                  <SelectTrigger aria-invalid={Boolean(fieldErrors.movement_type)} className={`h-9 rounded-sm mt-1 ${fieldErrors.movement_type ? "border-red-400 focus:ring-red-500" : ""}`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="in">Nhập kho</SelectItem>
                    <SelectItem value="out">Xuất kho</SelectItem>
                    <SelectItem value="adjust">Điều chỉnh</SelectItem>
                  </SelectContent>
                </Select>
                <FieldError message={fieldErrors.movement_type} />
              </div>
              <div>
                <Label className="text-xs">Chi nhánh</Label>
                <Select value={form.branch_id} onValueChange={(v) => setField("branch_id", v)}>
                  <SelectTrigger aria-invalid={Boolean(fieldErrors.branch_id)} className={`h-9 rounded-sm mt-1 ${fieldErrors.branch_id ? "border-red-400 focus:ring-red-500" : ""}`}><SelectValue placeholder="Chọn" /></SelectTrigger>
                  <SelectContent>{branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
                </Select>
                <FieldError message={fieldErrors.branch_id} />
              </div>
              <Field label="Số lượng *" type="number" value={form.qty} error={fieldErrors.qty} onChange={(v) => setField("qty", v)} testid="f-stock-qty" />
              <Field label="Đơn giá vốn" type="number" value={form.unit_cost} error={fieldErrors.unit_cost} onChange={(v) => setField("unit_cost", v)} />
            </div>
            <Field label="Ghi chú" value={form.note} error={fieldErrors.note} onChange={(v) => setField("note", v)} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Hủy</Button>
            <Button onClick={submitMove} className="bg-emerald-800 hover:bg-emerald-900 rounded-sm" data-testid="btn-save-move">Ghi nhận</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testid, error }) {
  return (
    <div>
      <Label className="text-xs">{label}</Label>
      <Input
        type={type}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        aria-invalid={Boolean(error)}
        className={`h-9 rounded-sm mt-1 ${error ? "border-red-400 focus-visible:ring-red-500" : ""}`}
        data-testid={testid}
      />
      <FieldError message={error} />
    </div>
  );
}

function FieldError({ message }) {
  return message ? <div className="text-xs text-red-700 mt-1">{message}</div> : null;
}
